import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { jwtDecode } from "jwt-decode";
import { getAuthToken, logout } from "utils/authToken";

import Box from "@mui/material/Box";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import ButtonBase from "@mui/material/ButtonBase";
import Avatar from "@mui/material/Avatar";
import Typography from "@mui/material/Typography";
import Icon from "@mui/material/Icon";

import AdminAnalytics from "./components/AdminAnalytics";
import AdminUsers from "./components/AdminUsers";
import AdminGoals from "./components/AdminGoals";
import AdminRecipes from "./components/AdminRecipes";

const NAV_W = 220;
const ACCENT = "#5c6bc0";

const TABS = [
  { label: "Analytics", icon: "bar_chart" },
  { label: "Users", icon: "people_alt" },
  { label: "Goals", icon: "emoji_events" },
  { label: "Recipes", icon: "restaurant_menu" },
  { label: "Content", icon: "edit_note" },
];

function ContentPlaceholder() {
  return (
    <Box
      display="flex"
      flexDirection="column"
      alignItems="center"
      justifyContent="center"
      minHeight={400}
      sx={{ color: "#aaa" }}
    >
      <Icon sx={{ fontSize: 52, mb: 2 }}>construction</Icon>
      <Typography variant="h6" fontWeight={600} color="text.secondary">
        Content Management
      </Typography>
      <Typography variant="body2" color="text.disabled" mt={0.5}>
        Recipe and goal creation tools — coming soon.
      </Typography>
    </Box>
  );
}

function Admin() {
  const [tab, setTab] = useState(0);
  const [username, setUsername] = useState("");
  const navigate = useNavigate();

  useEffect(() => {
    const token = getAuthToken();
    if (!token) {
      navigate("/authentication/sign-in", { replace: true });
      return;
    }
    try {
      const decoded = jwtDecode(token);
      if (decoded.role !== "admin") {
        navigate("/dashboard", { replace: true });
        return;
      }
      setUsername(decoded.username || "Admin");
    } catch {
      navigate("/authentication/sign-in", { replace: true });
    }
  }, [navigate]);

  const handleLogout = () => {
    logout();
    navigate("/authentication/sign-in", { replace: true });
  };

  const renderTab = () => {
    switch (tab) {
      case 0:
        return <AdminAnalytics />;
      case 1:
        return <AdminUsers />;
      case 2:
        return <AdminGoals />;
      case 3:
        return <AdminRecipes />;
      case 4:
        return <ContentPlaceholder />;
      default:
        return null;
    }
  };

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f4f5f7" }}>
      {/* Sidebar */}
      <Box
        sx={{
          width: NAV_W,
          flexShrink: 0,
          bgcolor: "#1a1d23",
          display: "flex",
          flexDirection: "column",
          position: "fixed",
          top: 0,
          left: 0,
          height: "100vh",
          zIndex: 100,
        }}
      >
        {/* Brand */}
        <Box sx={{ px: 3, pt: 3.5, pb: 2 }}>
          <Typography
            variant="subtitle2"
            sx={{ color: "#fff", fontWeight: 800, letterSpacing: 1.5, fontSize: "0.7rem" }}
          >
            ESTIA
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b7280", fontSize: "0.72rem", mt: 0.25 }}>
            Admin Console
          </Typography>
        </Box>

        <Box sx={{ mx: 2, mb: 2, height: "1px", bgcolor: "#2d3139" }} />

        {/* Nav items */}
        <Box sx={{ flex: 1, px: 1.5 }}>
          {TABS.map((t, i) => {
            const active = tab === i;
            return (
              <ButtonBase
                key={t.label}
                onClick={() => setTab(i)}
                sx={{
                  width: "100%",
                  display: "flex",
                  alignItems: "center",
                  gap: 1.5,
                  px: 1.5,
                  py: 1,
                  mb: 0.5,
                  borderRadius: "8px",
                  bgcolor: active ? "#2a2d35" : "transparent",
                  transition: "background 0.15s",
                  "&:hover": { bgcolor: "#22252d" },
                  textAlign: "left",
                }}
              >
                <Icon
                  sx={{
                    fontSize: "1.1rem !important",
                    color: active ? ACCENT : "#6b7280",
                    transition: "color 0.15s",
                  }}
                >
                  {t.icon}
                </Icon>
                <Typography
                  variant="body2"
                  sx={{
                    color: active ? "#fff" : "#9ca3af",
                    fontWeight: active ? 600 : 400,
                    fontSize: "0.82rem",
                    transition: "color 0.15s",
                  }}
                >
                  {t.label}
                </Typography>
                {active && (
                  <Box
                    sx={{
                      ml: "auto",
                      width: 3,
                      height: 16,
                      borderRadius: 4,
                      bgcolor: ACCENT,
                    }}
                  />
                )}
              </ButtonBase>
            );
          })}
        </Box>

        {/* User + logout */}
        <Box sx={{ p: 2, borderTop: "1px solid #2d3139" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, mb: 1.5 }}>
            <Avatar sx={{ width: 30, height: 30, bgcolor: ACCENT, fontSize: "0.75rem" }}>
              {username ? username[0].toUpperCase() : "A"}
            </Avatar>
            <Typography variant="body2" sx={{ color: "#d1d5db", fontSize: "0.8rem" }}>
              {username || "Admin"}
            </Typography>
          </Box>
          <ButtonBase
            onClick={handleLogout}
            sx={{
              width: "100%",
              display: "flex",
              alignItems: "center",
              gap: 1,
              px: 1.5,
              py: 0.75,
              borderRadius: "8px",
              color: "#6b7280",
              "&:hover": { bgcolor: "#22252d", color: "#9ca3af" },
              transition: "all 0.15s",
            }}
          >
            <Icon sx={{ fontSize: "1rem !important" }}>logout</Icon>
            <Typography variant="body2" sx={{ fontSize: "0.78rem" }}>
              Log out
            </Typography>
          </ButtonBase>
        </Box>
      </Box>

      {/* Main content */}
      <Box
        sx={{
          ml: `${NAV_W}px`,
          flex: 1,
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Page header */}
        <Box
          sx={{
            px: { xs: 3, md: 5 },
            pt: 4,
            pb: 3,
            bgcolor: "#f4f5f7",
          }}
        >
          <Typography variant="h5" fontWeight={700} sx={{ color: "#111827" }}>
            {TABS[tab].label}
          </Typography>
          <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.25 }}>
            {tab === 0 && "Platform-wide statistics"}
            {tab === 1 && "Manage all registered users"}
            {tab === 2 && "View and edit goals"}
            {tab === 3 && "Browse and manage recipes"}
            {tab === 4 && "Recipe and goal creation"}
          </Typography>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, px: { xs: 3, md: 5 }, pb: 6 }}>{renderTab()}</Box>
      </Box>
    </Box>
  );
}

export default Admin;
